import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useRef, useState } from 'react';
import { Tooltip } from '@mui/material';

import { getFolderAccessessById, getVideofeedAccessess } from '../../../api';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { FILE_ACCESS_TYPE } from '../../../enums/TYPES';
import ShareButton from '../ShareButton';
import styles from './ShareAccessButton.module.scss';

type ShareAccessButtonProps = {
  targetId: string;
  targetType: 'folder' | 'videofeed';
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const ShareAccessButton: React.FC<ShareAccessButtonProps> = ({ targetId, targetType, onClick }) => {
  const canViewEmails = useCanViewEmails();
  const [access, setAccess] = useState<FILE_ACCESS_TYPE[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dragTooltipTarget = useRef<{ element: HTMLElement; title: string } | null>(null);

  const hideParentDragTooltip = (event: React.MouseEvent<HTMLButtonElement>) => {
    const element = event.currentTarget.closest<HTMLElement>('[draggable="true"][title]');
    if (!element) return;
    dragTooltipTarget.current = { element, title: element.title };
    element.removeAttribute('title');
  };

  const restoreParentDragTooltip = () => {
    if (dragTooltipTarget.current?.element.isConnected) {
      dragTooltipTarget.current.element.title = dragTooltipTarget.current.title;
    }
    dragTooltipTarget.current = null;
  };

  const loadAccess = async () => {
    setLoading(true);
    try {
      const response =
        targetType === 'folder'
          ? await getFolderAccessessById(targetId, true)
          : await getVideofeedAccessess(targetId, true);
      setAccess(response.data?.effective_access || response.data?.access || []);
    } catch {
      setAccess([]);
    } finally {
      setLoaded(true);
      setLoading(false);
    }
  };

  const activeAccess = access.filter(
    (item) => !(item?.access?.title === ACCESS_TYPE_ENUM.WITHOUT_ACCESS && item?.excluded),
  );
  const users = activeAccess.filter((item) => item.type === 'user' && item.user);
  const groups = activeAccess.filter((item) => item.type === 'group' && item.group);
  const sharedToAllUsers = activeAccess.some((item) => item.type === 'organization' && item.organization);

  const tooltip = loading ? (
    'Loading...'
  ) : users.length || groups.length || sharedToAllUsers ? (
    <div className={styles.tooltip}>
      <div className={styles.title}>Shared with:</div>
      {sharedToAllUsers && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>All Users</div>
        </div>
      )}
      {users.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Users</div>
          {users.map((item) => (
            <div key={`user-${item.id}`}>{item.user?.username || (canViewEmails ? item.user?.email : 'User')}</div>
          ))}
        </div>
      )}
      {groups.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Groups</div>
          {groups.map((item) => (
            <div key={`group-${item.id}`}>{item.group?.title}</div>
          ))}
        </div>
      )}
    </div>
  ) : loaded ? (
    'Not shared with anyone'
  ) : (
    'Show sharing details'
  );

  return (
    <Tooltip arrow title={tooltip} onOpen={loadAccess}>
      <button
        type="button"
        className={styles.button}
        onClick={onClick}
        onMouseEnter={hideParentDragTooltip}
        onMouseLeave={restoreParentDragTooltip}
        aria-label="Open share options"
      >
        <ShareButton />
      </button>
    </Tooltip>
  );
};

export default ShareAccessButton;
